import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, InputGroup, Alert } from 'react-bootstrap';
import { useResetPasswordUserMutation } from '../slices/usersApiSlice';
import StayTop from '../components/StayTop';
import Loader from '../components/Loader';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ResetPasswordForm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resetPasswordUser, { isLoading }] = useResetPasswordUserMutation();

    // useCallback to ensure this function is memoized and not recreated on every render
    const validatePasswordComplexity = useCallback((password) => {
        const errors = [];
        if (!/[a-z]/.test(password)) errors.push("At least one lowercase letter");
        if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
        if (!/\d/.test(password)) errors.push("At least one digit");
        if (!/[!@#$%^&*]/.test(password)) errors.push("At least one special character (!@#$%^&*)");
        if (password.length < 8) errors.push("Minimum length of 8 characters");
        return errors;
    }, []); // Dependencies are empty as this function does not depend on any state or props

    // useCallback to ensure this function is memoized and not recreated on every render
    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordErrors(validatePasswordComplexity(newPassword));
        setError(''); // Clear error when user types
        setSuccess(''); // Clear success when user types
    };

    // useCallback to ensure this function is memoized and not recreated on every render
    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        setError(''); // Clear error when user types
        setSuccess(''); // Clear success when user types
    };

    // Prevent pasting into the password field
    const handlePasswordPaste = (e) => {
        e.preventDefault();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); // Clear any previous errors
        setSuccess(''); // Clear any previous success messages

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        if (passwordErrors.length > 0) {
            setError("Please fix the password requirements listed below");
            return;
        }

        try {
            await resetPasswordUser({ token, password }).unwrap();
            setSuccess('Your password has been reset successfully! Redirecting to login...');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            setError(err?.data?.message || 'An error occurred while resetting your password. Please try again later.');
        }
    };

    return (
        <>
            <StayTop />
            <Link to="/" className="btn btn-light my-3 orange-outline">
                Go Home
            </Link>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success">
                    {success}
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <h4>Reset Your Password</h4>
                <Form.Group className="mb-3" controlId="password">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                        <div style={{ width: '30%' }}> {/* Adjust the width here */}
                            <Form.Control
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter new password"
                                value={password}
                                onChange={handlePasswordChange}
                                onPaste={handlePasswordPaste} // Disable paste functionality
                                required
                            />
                        </div>
                        <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                    </InputGroup>
                    {passwordErrors.length > 0 && password && (
                        <div style={{ color: 'red', marginTop: '8px' }}>
                            <strong>Password requirements:</strong>
                            {passwordErrors.map((error, index) => <div key={index}>• {error}</div>)}
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="confirmPassword">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup>
                        <div style={{ width: '30%' }}> {/* Adjust the width here */}
                            <Form.Control
                                type={showPassword ? "text" : "password"}
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                onPaste={handlePasswordPaste} // Disable paste functionality
                                required
                            />
                        </div>
                        <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                    </InputGroup>
                    {password !== confirmPassword && confirmPassword && (
                        <div style={{ color: 'red', marginTop: '8px' }}>
                            Passwords do not match
                        </div>
                    )}
                </Form.Group>

                {isLoading && <Loader />}
                <Button
                    type="submit"
                    className="mt-2 blue-outline"
                    disabled={isLoading || passwordErrors.length > 0
                        || password !== confirmPassword || !password || !confirmPassword}>
                    Reset Password
                </Button>
            </Form>
        </>
    );
};

export default ResetPasswordForm;